import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { publicAPI } from '../services/api';
import {
  FiHome, FiUsers, FiFileText, FiDollarSign, FiBarChart2, FiBell,
  FiCheck, FiX, FiArrowRight, FiStar, FiMapPin, FiShield, FiZap,
  FiSmartphone, FiGlobe, FiTool, FiChevronRight, FiAward, FiClock,
  FiTrendingUp, FiHeart, FiLock, FiMail, FiMenu, FiSearch, FiSliders,
  FiVideo, FiMaximize2, FiPhone, FiMessageCircle, FiLayers, FiRefreshCw
} from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './auth/auth.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const MALI_CITIES = [
  'Bamako', 'Sikasso', 'Ségou', 'Koulikoro', 'Mopti', 'Kayes', 'Gao', 'Tombouctou', 'Koutiala', 'San', 'Kati'
];

const MALI_POPULAR_NEIGHBORHOODS = [
  'ACI 2000', 'Badalabougou', 'Baco Djicoroni', 'Hamdallaye', 'Yirimadio', 'Sébénikoro', 'Sotuba',
  'Hippodrome', 'Faladié', 'Torokorobougou', 'Golf', 'Kalaban Coro', 'Titibougou', 'Moribabougou',
  'Niamana', 'Missabougou', 'Korofina', 'Magnambougou', 'Djélibougou', 'Daoudabougou',
  'Quartier du Fleuve', 'Quartier Mali', 'Lafiabougou', 'Hamdallaye ACI', 'Banankabougou', 'Sabalibougou', 'Senou'
];

const PROPERTY_TYPES = [
  { label: 'Tous les types', value: '' },
  { label: 'Appartement', value: 'Appartement' },
  { label: 'Villa', value: 'Villa' },
  { label: 'Maison', value: 'Maison' },
  { label: 'Studio / Chambre-Salon', value: 'Studio' },
  { label: 'Terrain / Parcelle', value: 'Terrain' },
  { label: 'Bureau & Commerce', value: 'Bureau' },
  { label: 'Immeuble', value: 'Immeuble' },
  { label: 'Duplex / Triplex', value: 'Duplex' },
];

const ROOM_OPTIONS = [
  { label: 'Toutes configurations', value: '' },
  { label: 'Studio (1 pièce)', value: 'studio' },
  { label: '2 pièces (1 chambre salon)', value: '2' },
  { label: '3 pièces (2 chambres salon)', value: '3' },
  { label: '4 pièces (3 chambres salon)', value: '4' },
  { label: '5+ pièces (4+ chambres)', value: '5+' },
];

const getAnnouncementCoords = (a) => {
  if (a.latitude && a.longitude) {
    return [parseFloat(a.latitude), parseFloat(a.longitude)];
  }
  const cityCoords = {
    'bamako': [12.6392, -8.0029],
    'sikasso': [11.3175, -5.6667],
    'mopti': [14.4958, -4.1833],
    'ségou': [13.4317, -6.2158],
    'segou': [13.4317, -6.2158],
    'koulikoro': [12.8628, -7.5599],
    'kayes': [14.4469, -11.4442],
    'gao': [16.2717, -0.0447],
    'tombouctou': [16.7736, -3.0074],
    'kati': [12.7441, -8.0726],
  };
  const city = String(a.city || '').trim().toLowerCase();
  if (cityCoords[city]) return cityCoords[city];
  return [12.6392, -8.0029];
};

/* ============================================================
   COMPOSANT CARTE DE BIEN ENRICHI (ANNOUNCEMENT CARD)
   ============================================================ */
const AnnouncementCard = ({ announcement, onContact, onSelectDetail }) => {
  const [currentIdx, setCurrentIdx] = useState(0);

  const mediaList = useMemo(() => {
    const list = [];
    if (announcement.videos && announcement.videos.length > 0) {
      announcement.videos.forEach(v => {
        if (v && v.url) list.push({ type: 'video', url: v.url, title: v.title });
      });
    }
    if (announcement.images && announcement.images.length > 0) {
      announcement.images.forEach(img => {
        if (img && img.url) list.push({ type: 'image', url: img.url });
      });
    } else if (announcement.image_url) {
      list.push({ type: 'image', url: announcement.image_url });
    }
    return list;
  }, [announcement]);

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIdx(prev => (prev === 0 ? mediaList.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIdx(prev => (prev === mediaList.length - 1 ? 0 : prev + 1));
  };

  const currentMedia = mediaList[currentIdx];
  const hasVideos = announcement.videos && announcement.videos.length > 0;

  return (
    <div 
      className="lp-bien-card" 
      onClick={() => onSelectDetail(announcement)}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', cursor: 'pointer' }}
    >
      {/* ── Image / Vidéo Carousel ── */}
      <div className="lp-bien-image-wrapper" style={{ position: 'relative', overflow: 'hidden', height: 230 }}>
        {mediaList.length > 0 ? (
          <div className="lp-bien-carousel" style={{ height: '100%', width: '100%' }}>
            {currentMedia.type === 'video' ? (
              <div className="lp-bien-carousel-item" style={{ height: '100%', width: '100%', position: 'relative' }}>
                <video
                  src={currentMedia.url}
                  controls
                  preload="metadata"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
                />
                <div className="lp-bien-video-overlay">🎥 Vidéo</div>
              </div>
            ) : (
              <div className="lp-bien-carousel-item" style={{ height: '100%', width: '100%' }}>
                <img src={currentMedia.url} alt={announcement.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
          </div>
        ) : (
          <div className="lp-bien-carousel-item" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
            background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)'
          }}>
            <span style={{ fontSize: 56, opacity: 0.3 }}>🏠</span>
          </div>
        )}

        {/* Badges Flottants */}
        <div className="lp-bien-badges">
          <span className={`lp-bien-type-badge ${announcement.transaction_type}`}>
            {announcement.transaction_type === 'sale' ? '🏷️ À Vendre' : '🔑 À Louer'}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            {announcement.is_featured && (
              <span className="lp-bien-featured-badge">⭐ Vedette</span>
            )}
            {hasVideos && (
              <span style={{ background: 'rgba(124, 58, 237, 0.9)', color: '#fff', padding: '4px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>
                🎥 Vidéo
              </span>
            )}
          </div>
        </div>

        {/* Navigation Carousel Flèches */}
        {mediaList.length > 1 && (
          <>
            <button 
              onClick={handlePrev} 
              style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%',
                width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', zIndex: 12, fontSize: 13
              }}
            >
              ❮
            </button>
            <button 
              onClick={handleNext} 
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%',
                width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', zIndex: 12, fontSize: 13
              }}
            >
              ❯
            </button>
          </>
        )}

        {/* Compteur Médias */}
        {mediaList.length > 1 && (
          <div className="lp-bien-media-count">
            📷 {mediaList.length} médias
          </div>
        )}
      </div>

      {/* ── Corps de la Carte ── */}
      <div className="lp-bien-content" style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#2563eb',
            background: '#eff6ff', padding: '3px 8px', borderRadius: 6
          }}>
            {announcement.property_type || 'Bien Immobilier'}
            {announcement.unit_number ? ` (Unité ${announcement.unit_number})` : ''}
          </span>
          {announcement.is_furnished && (
            <span style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#dcfce7', padding: '2px 6px', borderRadius: 6 }}>
              🛋️ Meublé
            </span>
          )}
        </div>

        <h3 className="lp-bien-title" style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: '4px 0 6px', minHeight: 44, lineHeight: 1.3 }}>
          {announcement.title}
        </h3>

        <div className="lp-bien-location" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: '0.85rem', marginBottom: 12 }}>
          <FiMapPin color="#ef4444" size={14} />
          <span style={{ fontWeight: 500 }}>
            {announcement.neighborhood ? `${announcement.neighborhood}, ` : ''}{announcement.city || 'Bamako'}
          </span>
        </div>

        {/* ── Spécifications Clés ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, color: '#475569', fontSize: '0.82rem', marginBottom: 16, background: '#f8fafc', padding: '8px 10px', borderRadius: 8 }}>
          {announcement.surface_area && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span>📐</span> {announcement.surface_area} m²
            </span>
          )}
          {(announcement.num_bedrooms || announcement.num_rooms) && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span>🛏️</span>
              {announcement.num_bedrooms 
                ? `${announcement.num_bedrooms} ch.${announcement.num_rooms ? ` (${announcement.num_rooms} pcs)` : ''}`
                : `${announcement.num_rooms} pièces`}
            </span>
          )}
          {announcement.num_bathrooms && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span>🚿</span> {announcement.num_bathrooms} sdb
            </span>
          )}
        </div>

        {/* ── Prix & Actions ── */}
        <div style={{ marginTop: 'auto', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#2563eb', letterSpacing: '-0.02em' }}>
                {parseInt(announcement.price || 0, 10).toLocaleString('fr-FR')} XOF
              </span>
              {announcement.transaction_type === 'rent' && (
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>Loyer mensuel</span>
              )}
            </div>

            <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => onSelectDetail(announcement)}
                className="btn btn-sm btn-outline"
                style={{ padding: '6px 12px', fontSize: '0.82rem', borderRadius: 8 }}
                title="Consulter la fiche complète"
              >
                Détails
              </button>
              <button
                onClick={() => onContact(announcement)}
                className="btn btn-sm btn-primary"
                style={{ padding: '6px 14px', fontSize: '0.82rem', borderRadius: 8, background: 'linear-gradient(135deg, #2563eb, #1d4ed8)' }}
              >
                Contacter
              </button>
            </div>
          </div>

          {/* Agence Mandataire */}
          {announcement.agency_name && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#64748b', background: '#ffffff', borderTop: '1px dashed #e2e8f0', paddingTop: 6 }}>
              <span style={{ fontWeight: 600, color: '#334155' }}>🏢 {announcement.agency_name}</span>
              {announcement.agency_phone && <span>📞 {announcement.agency_phone}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   MODAL DE FICHE DÉTAILLÉE COMPLÈTE DU BIEN (PROPERTY DETAIL MODAL)
   ============================================================ */
const PropertyDetailModal = ({ property, onClose, onContact }) => {
  const [activeMediaIdx, setActiveMediaIdx] = useState(0);

  if (!property) return null;

  const mediaList = [];
  if (property.videos && property.videos.length > 0) {
    property.videos.forEach(v => v?.url && mediaList.push({ type: 'video', url: v.url, title: v.title }));
  }
  if (property.images && property.images.length > 0) {
    property.images.forEach(img => img?.url && mediaList.push({ type: 'image', url: img.url }));
  } else if (property.image_url) {
    mediaList.push({ type: 'image', url: property.image_url });
  }

  const currentMedia = mediaList[activeMediaIdx] || mediaList[0];
  const coords = getAnnouncementCoords(property);

  return (
    <div className="lp-detail-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lp-detail-modal-content">
        {/* Header */}
        <div className="lp-detail-modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                background: property.transaction_type === 'sale' ? '#dbeafe' : '#dcfce7',
                color: property.transaction_type === 'sale' ? '#1e40af' : '#166534',
              }}>
                {property.transaction_type === 'sale' ? '🏷️ À VENDRE' : '🔑 À LOUER'}
              </span>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                Réf. #{property.id?.slice(0, 8)}
              </span>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, margin: '6px 0 0', color: '#0f172a' }}>
              {property.title}
            </h2>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 16, fontWeight: 700, color: '#475569' }}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="lp-detail-modal-body">
          {/* Visionneuse Média Principale */}
          {mediaList.length > 0 ? (
            <div style={{ marginBottom: 20 }}>
              <div style={{ height: 380, borderRadius: 16, overflow: 'hidden', background: '#0f172a', position: 'relative' }}>
                {currentMedia.type === 'video' ? (
                  <video src={currentMedia.url} controls autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <img src={currentMedia.url} alt={property.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </div>

              {/* Miniatures */}
              {mediaList.length > 1 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10, overflowX: 'auto', paddingBottom: 4 }}>
                  {mediaList.map((m, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveMediaIdx(idx)}
                      style={{
                        width: 70, height: 50, borderRadius: 8, overflow: 'hidden', border: activeMediaIdx === idx ? '3px solid #2563eb' : '1px solid #cbd5e1',
                        padding: 0, cursor: 'pointer', flexShrink: 0, position: 'relative'
                      }}
                    >
                      {m.type === 'video' ? (
                        <div style={{ background: '#1e293b', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12 }}>
                          🎥
                        </div>
                      ) : (
                        <img src={m.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {/* Prix & Localisation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Prix du bien</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#2563eb' }}>
                {parseInt(property.price || 0, 10).toLocaleString('fr-FR')} XOF
                {property.transaction_type === 'rent' && <span style={{ fontSize: 14, fontWeight: 500, color: '#64748b' }}> / mois</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                <FiMapPin color="#ef4444" /> {property.neighborhood ? `${property.neighborhood}, ` : ''}{property.city || 'Bamako'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                {property.address || 'Mali'}
              </div>
            </div>
          </div>

          {/* Grille des Spécifications & Caractéristiques complètes */}
          <div className="lp-specs-pill-grid">
            <div className="lp-spec-pill">
              <span className="lp-spec-pill-label">Type de bien</span>
              <span className="lp-spec-pill-value">{property.property_type || 'Maison / Villa'}</span>
            </div>
            <div className="lp-spec-pill">
              <span className="lp-spec-pill-label">Superficie</span>
              <span className="lp-spec-pill-value">{property.surface_area ? `${property.surface_area} m²` : 'Non précisée'}</span>
            </div>
            <div className="lp-spec-pill">
              <span className="lp-spec-pill-label">Chambres (Salon)</span>
              <span className="lp-spec-pill-value">
                {property.num_bedrooms 
                  ? `${property.num_bedrooms} chambres${property.num_rooms ? ` (${property.num_rooms} pièces)` : ''}`
                  : (property.num_rooms ? `${property.num_rooms} pièces` : '—')}
              </span>
            </div>
            <div className="lp-spec-pill">
              <span className="lp-spec-pill-label">Salles de bain</span>
              <span className="lp-spec-pill-value">{property.num_bathrooms ? `${property.num_bathrooms} sdb` : '—'}</span>
            </div>
            <div className="lp-spec-pill">
              <span className="lp-spec-pill-label">Ameublement</span>
              <span className="lp-spec-pill-value" style={{ color: property.is_furnished ? '#16a34a' : 'inherit' }}>
                {property.is_furnished ? '🛋️ Meublé' : 'Non meublé'}
              </span>
            </div>
            {property.unit_number && (
              <div className="lp-spec-pill">
                <span className="lp-spec-pill-label">Unité / Porte</span>
                <span className="lp-spec-pill-value">N° {property.unit_number}</span>
              </div>
            )}
            {property.year_built && (
              <div className="lp-spec-pill">
                <span className="lp-spec-pill-label">Année</span>
                <span className="lp-spec-pill-value">{property.year_built}</span>
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ marginTop: 20 }}>
            <h4 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Description du bien</h4>
            <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
              {property.description || "Aucune description détaillée n'a été fournie pour cette annonce."}
            </p>
          </div>

          {/* Localisation Mini Carte */}
          <div style={{ marginTop: 24 }}>
            <h4 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Localisation géographique</h4>
            <div style={{ height: 200, borderRadius: 14, overflow: 'hidden', border: '1px solid #cbd5e1' }}>
              <MapContainer center={coords} zoom={13} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={coords}>
                  <Popup>{property.title} — {property.city}</Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>

          {/* Bloc Agence & Actions Directes */}
          <div className="lp-agency-card-box">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                🏢
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Agence Mandataire</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{property.agency_name || 'Agence Partenaire MaliImmo'}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{property.agency_address || 'Bamako, Mali'}</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {property.agency_phone && (
                <a
                  href={`tel:${property.agency_phone}`}
                  className="btn btn-outline"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
                >
                  <FiPhone /> {property.agency_phone}
                </a>
              )}
              {property.agency_phone && (
                <a
                  href={`https://wa.me/${property.agency_phone.replace(/\D/g, '')}?text=${encodeURIComponent(
                    `Bonjour, je suis intéressé par votre bien : ${property.title} (${property.city} ${property.neighborhood || ''}) - Réf #${property.id?.slice(0, 8)}. Pourriez-vous me donner plus de détails ?`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn"
                  style={{ background: '#22c55e', color: '#ffffff', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', border: 'none' }}
                >
                  <FiMessageCircle /> WhatsApp
                </a>
              )}
              <button
                onClick={() => { onClose(); onContact(property); }}
                className="btn btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <FiMail /> Demander une visite
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   PAGE D'ACCUEIL PRINCIPALE (LANDING PAGE)
   ============================================================ */
const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [animatedCounts, setAnimatedCounts] = useState({ agencies: 0, properties: 0, satisfaction: 0, countries: 0 });
  const statsRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Annonces & Vue
  const [publicAnnouncements, setPublicAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapView, setMapView] = useState(false);

  // États de recherche & Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [selectedRooms, setSelectedRooms] = useState('');
  const [filterType, setFilterType] = useState(''); // '' | 'rent' | 'sale'
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minSurface, setMinSurface] = useState('');
  const [isFurnishedOnly, setIsFurnishedOnly] = useState(false);
  const [hasVideoOnly, setHasVideoOnly] = useState(false);
  const [sortBy, setSortBy] = useState('featured'); // 'featured' | 'recent' | 'price_asc' | 'price_desc' | 'views'
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Modals
  const [selectedPropertyDetail, setSelectedPropertyDetail] = useState(null);
  const [contactModal, setContactModal] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [contactSent, setContactSent] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState([]);

  // Redirection si connecté
  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  // Navbar scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animation stats
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const targets = { agencies: 500, properties: 12000, satisfaction: 98, countries: 4 };
          const duration = 2000;
          const step = 20;
          const increments = {
            agencies: targets.agencies / (duration / step),
            properties: targets.properties / (duration / step),
            satisfaction: targets.satisfaction / (duration / step),
            countries: targets.countries / (duration / step),
          };
          let current = { agencies: 0, properties: 0, satisfaction: 0, countries: 0 };
          const timer = setInterval(() => {
            current = {
              agencies: Math.min(current.agencies + increments.agencies, targets.agencies),
              properties: Math.min(current.properties + increments.properties, targets.properties),
              satisfaction: Math.min(current.satisfaction + increments.satisfaction, targets.satisfaction),
              countries: Math.min(current.countries + increments.countries, targets.countries),
            };
            setAnimatedCounts({ ...current });
            if (current.agencies >= targets.agencies && current.properties >= targets.properties) {
              clearInterval(timer);
            }
          }, step);
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [hasAnimated]);

  // Chargement des annonces publiques
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await publicAPI.getAnnouncements({ limit: 100 });
      setPublicAnnouncements(response.data || []);
    } catch (error) {
      console.error('Erreur chargement annonces:', error);
      setPublicAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Extraire les villes et quartiers réels présents dans les données
  const availableNeighborhoods = useMemo(() => {
    const set = new Set(MALI_POPULAR_NEIGHBORHOODS);
    publicAnnouncements.forEach(a => {
      if (a.neighborhood && a.neighborhood.trim()) {
        set.add(a.neighborhood.trim());
      }
    });
    return Array.from(set).sort();
  }, [publicAnnouncements]);

  // Moteur de recherche sémantique & filtrage multi-détails
  const filteredAnnouncements = useMemo(() => {
    return publicAnnouncements.filter(item => {
      // 1. Filtre Transaction (Location / Vente)
      if (filterType && item.transaction_type !== filterType) return false;

      // 2. Filtre Ville
      if (selectedCity && String(item.city || '').toLowerCase() !== selectedCity.toLowerCase()) {
        return false;
      }

      // 3. Filtre Quartier
      if (selectedNeighborhood && !String(item.neighborhood || '').toLowerCase().includes(selectedNeighborhood.toLowerCase())) {
        return false;
      }

      // 4. Filtre Type de Bien
      if (selectedPropertyType && !String(item.property_type || '').toLowerCase().includes(selectedPropertyType.toLowerCase())) {
        return false;
      }

      // 5. Filtre Pièces / Chambres
      if (selectedRooms) {
        const rooms = parseInt(item.num_rooms || 0, 10);
        const bedrooms = parseInt(item.num_bedrooms || 0, 10);
        if (selectedRooms === 'studio' && rooms !== 1 && bedrooms !== 1 && !String(item.property_type).toLowerCase().includes('studio')) return false;
        if (selectedRooms === '2' && rooms !== 2 && bedrooms !== 1) return false; // 1 ch + 1 salon = 2 pièces
        if (selectedRooms === '3' && rooms !== 3 && bedrooms !== 2) return false; // 2 ch + 1 salon = 3 pièces
        if (selectedRooms === '4' && rooms !== 4 && bedrooms !== 3) return false; // 3 ch + 1 salon = 4 pièces
        if (selectedRooms === '5+' && rooms < 5 && bedrooms < 4) return false;
      }

      // 6. Filtre Prix Min & Max
      const price = parseFloat(item.price || 0);
      if (minPrice && price < parseFloat(minPrice)) return false;
      if (maxPrice && price > parseFloat(maxPrice)) return false;

      // 7. Filtre Superficie Min
      if (minSurface && parseFloat(item.surface_area || 0) < parseFloat(minSurface)) return false;

      // 8. Filtre Meublé uniquement
      if (isFurnishedOnly && !item.is_furnished) return false;

      // 9. Filtre Vidéo uniquement
      if (hasVideoOnly && (!item.videos || item.videos.length === 0)) return false;
       
      // 10. Recherche Textuelle & Sémantique intelligente
      if (searchQuery && searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const fullText = [
          item.title, item.description, item.city, item.neighborhood, item.address,
          item.property_type, item.unit_number ? `unité ${item.unit_number}` : '',
          item.agency_name, item.is_furnished ? 'meublé meuble' : 'non meublé',
          item.transaction_type === 'sale' ? 'vente acheter achat' : 'location louer loyer',
        ].filter(Boolean).join(' ').toLowerCase();

        // Si la requête match textuellement
        if (fullText.includes(q)) return true;

        // Détections sémantiques locales ("2 chambres salon", "chambre salon"...)
        if ((q.includes('2 chambre') || q.includes('deux chambre') || q.includes('2 ch') || q.includes('2ch')) &&
            (parseInt(item.num_bedrooms, 10) === 2 || parseInt(item.num_rooms, 10) === 3)) {
          return true;
        }
        if ((q.includes('1 chambre') || q.includes('une chambre') || q.includes('chambre salon')) &&
            (parseInt(item.num_bedrooms, 10) === 1 || parseInt(item.num_rooms, 10) === 2)) {
          return true;
        }
        if ((q.includes('3 chambre') || q.includes('trois chambre')) &&
            (parseInt(item.num_bedrooms, 10) === 3 || parseInt(item.num_rooms, 10) === 4)) {
          return true;
        }

        // Token split
        const tokens = q.split(/\s+/).filter(t => t.length > 1);
        if (tokens.length > 0 && !tokens.every(token => fullText.includes(token))) {
          return false;
        }
      }

      return true;
    }).sort((a, b) => {
      if (sortBy === 'price_asc') return parseFloat(a.price || 0) - parseFloat(b.price || 0);
      if (sortBy === 'price_desc') return parseFloat(b.price || 0) - parseFloat(a.price || 0);
      if (sortBy === 'views') return (b.views_count || 0) - (a.views_count || 0);
      if (sortBy === 'recent') return new Date(b.published_at || b.created_at || 0) - new Date(a.published_at || a.created_at || 0);
      // 'featured'
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
      return new Date(b.published_at || b.created_at || 0) - new Date(a.published_at || a.created_at || 0);
    });
  }, [
    publicAnnouncements, filterType, selectedCity, selectedNeighborhood,
    selectedPropertyType, selectedRooms, minPrice, maxPrice, minSurface,
    isFurnishedOnly, hasVideoOnly, searchQuery, sortBy
  ]);

  // Nombre de filtres actifs
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (searchQuery) count++;
    if (selectedCity) count++;
    if (selectedNeighborhood) count++;
    if (selectedPropertyType) count++;
    if (selectedRooms) count++;
    if (filterType) count++;
    if (minPrice) count++;
    if (maxPrice) count++;
    if (minSurface) count++;
    if (isFurnishedOnly) count++;
    if (hasVideoOnly) count++;
    return count;
  }, [searchQuery, selectedCity, selectedNeighborhood, selectedPropertyType, selectedRooms, filterType, minPrice, maxPrice, minSurface, isFurnishedOnly, hasVideoOnly]);

  const resetAllFilters = () => {
    setSearchQuery('');
    setSelectedCity('');
    setSelectedNeighborhood('');
    setSelectedPropertyType('');
    setSelectedRooms('');
    setFilterType('');
    setMinPrice('');
    setMaxPrice('');
    setMinSurface('');
    setIsFurnishedOnly(false);
    setHasVideoOnly(false);
    setSortBy('featured');
  };

  // Contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await publicAPI.sendContact(contactModal.id, contactForm);
      setContactSent(true);
    } catch {
      setContactSent(true);
    }
  };

  const closeContactModal = () => {
    setContactModal(null);
    setContactSent(false);
    setContactForm({ name: '', phone: '', email: '', message: '' });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const features = [
    { icon: <FiHome />, color: '#dbeafe', iconColor: '#2563eb', title: 'Gestion des Biens', desc: 'Gérez votre portefeuille immobilier : appartements, terrains, villas. Photos, vidéos, géolocalisation et statuts en temps réel.' },
    { icon: <FiFileText />, color: '#dcfce7', iconColor: '#16a34a', title: 'Cycle Locatif Complet', desc: 'De la création du contrat à l\'archivage : validation, signature, activation, paiements mensuels automatiques et relances.' },
    { icon: <FiDollarSign />, color: '#fef9c3', iconColor: '#ca8a04', title: 'Gestion Financière', desc: 'Suivi des loyers, facturation, commissions, exports CSV/PDF. Tableau de bord financier avec indicateurs clés.' },
    { icon: <FiUsers />, color: '#fce7f3', iconColor: '#db2777', title: 'CRM Immobilier', desc: 'Gérez vos clients, prospects et leads. Pipeline de vente, historique des interactions, mandats et rappels automatiques.' },
    { icon: <FiGlobe />, color: '#ede9fe', iconColor: '#7c3aed', title: 'Publication d\'Annonces', desc: 'Publiez vos biens avec SEO optimisé, partage WhatsApp/réseaux sociaux, favoris, alertes et visite virtuelle.' },
    { icon: <FiBarChart2 />, color: '#ffedd5', iconColor: '#ea580c', title: 'Rapports & Analytics', desc: 'Statistiques détaillées, taux d\'occupation, revenus par période, performance des agents et exports financiers.' },
  ];

  const steps = [
    { num: '01', icon: '🏢', title: 'Créez votre agence', desc: 'Inscrivez votre agence en 2 minutes. Configurez votre équipe et vos préférences.' },
    { num: '02', icon: '🏠', title: 'Ajoutez vos biens', desc: 'Importez vos biens, fixez les loyers, ajoutez les photos/vidéos et assignez les propriétaires.' },
    { num: '03', icon: '🚀', title: 'Pilotez votre activité', desc: 'Générez les contrats, encaissez les loyers, suivez les paiements et développez votre agence.' },
  ];

  const plans = [
    { name: 'Starter', price: 'Gratuit', period: 'pour toujours', desc: 'Idéal pour démarrer ou pour les petites agences', badge: null, featured: false, cta: 'Commencer gratuitement', ctaClass: 'outline', features: [{ ok: true, text: 'Jusqu\'à 5 biens' }, { ok: true, text: '1 utilisateur' }, { ok: true, text: 'Gestion des contrats' }, { ok: false, text: 'Paiements automatiques' }, { ok: false, text: 'Rapports avancés' }] },
    { name: 'Professionnel', price: '25 000', period: '/mois', desc: 'Pour les agences en pleine croissance', badge: 'Populaire', featured: true, cta: 'Démarrer l\'essai 30 jours', ctaClass: 'primary', features: [{ ok: true, text: 'Jusqu\'à 50 biens' }, { ok: true, text: '5 utilisateurs' }, { ok: true, text: 'Paiements & Quittances automatiques' }, { ok: true, text: 'Publication d\'annonces' }, { ok: true, text: 'Support prioritaire' }] },
    { name: 'Entreprise', price: '65 000', period: '/mois', desc: 'Pour les grands réseaux et gestionnaires de patrimoine', badge: 'Illimité', featured: false, cta: 'Nous contacter', ctaClass: 'outline', features: [{ ok: true, text: 'Biens illimités' }, { ok: true, text: 'Utilisateurs illimités' }, { ok: true, text: 'Multi-agences & Réseau' }, { ok: true, text: 'API & Intégrations personnalisées' }, { ok: true, text: 'Accompagnement dédié' }] },
  ];

  const testimonials = [
    { name: 'Mamadou Traoré', role: 'Directeur Général, Mandé Immobilier', text: 'GestionImmo a réduit nos retards de paiement de 60% dès le premier trimestre. L\'outil indispensable pour les agences à Bamako.', initials: 'MT', stars: 5 },
    { name: 'Fatoumata Coulibaly', role: 'Gérante, Sahel Habitat Bamako', text: 'La simplicité de génération des contrats et la publication instantanée d\'annonces avec photos et vidéos nous font gagner des heures chaque jour.', initials: 'FC', stars: 5 },
    { name: 'Ousmane Diarra', role: 'Fondateur, Ségou Prestige Immo', text: 'La vue carte et la recherche détaillée par quartier nous permettent de trouver en 2 secondes le bien idéal pour nos clients.', initials: 'OD', stars: 5 },
  ];

  return (
    <div className="landing-page">
      {/* NAVBAR */}
      <nav className={`lp-navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="lp-container">
          <Link to="/" className="lp-navbar-brand">
            <div className="lp-navbar-brand-icon">🏢</div>
            <span>Gestion<span style={{ color: '#2563eb' }}>Immo</span></span>
          </Link>

          <button className="lp-mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>

          <div className={`lp-navbar-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <ul className="lp-navbar-links">
              <li><button onClick={() => scrollToSection('biens')} className="lp-nav-link">Biens disponibles</button></li>
              <li><button onClick={() => scrollToSection('features')} className="lp-nav-link">Fonctionnalités</button></li>
              <li><button onClick={() => scrollToSection('how-it-works')} className="lp-nav-link">Comment ça marche</button></li>
              <li><button onClick={() => scrollToSection('pricing')} className="lp-nav-link">Tarifs</button></li>
              <li><button onClick={() => scrollToSection('testimonials')} className="lp-nav-link">Témoignages</button></li>
            </ul>

            <div className="lp-navbar-actions">
              <Link to="/portal/login" className="lp-btn-ghost" title="Espace Client/Locataire">
                Espace Client
              </Link>
              <Link to="/login" className="lp-btn-ghost">Se connecter</Link>
              <Link to="/register" className="lp-btn-primary">Essai gratuit</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="lp-hero">
        <div className="lp-container">
          <div className="lp-hero-content">
            <div className="lp-hero-text">
              <div className="lp-hero-badge">
                <div className="lp-hero-badge-dot" />
                Plateforme SaaS #1 en Afrique de l'Ouest
              </div>
              <h1>
                Trouvez ou gérez votre bien<br />
                immobilière <span className="gradient-text">intelligemment</span>
              </h1>
              <p className="lp-hero-subtitle">
                La plateforme complète au Mali pour louer, acheter, vendre et administrer vos propriétés avec recherche géolocalisée et suivi notarié.
              </p>
              <div className="lp-hero-actions">
                <button onClick={() => scrollToSection('biens')} className="lp-btn-hero">
                  Explorer les annonces <FiArrowRight />
                </button>
                <Link to="/register" className="lp-btn-outline-white">
                  Créer un compte Agence
                </Link>
              </div>
              <div className="lp-hero-stats">
                <div className="lp-hero-stat">
                  <div className="lp-hero-stat-value">{Math.floor(animatedCounts.agencies)}+</div>
                  <div className="lp-hero-stat-label">Agences actives</div>
                </div>
                <div className="lp-hero-stat">
                  <div className="lp-hero-stat-value">{Math.floor(animatedCounts.properties / 1000)}K+</div>
                  <div className="lp-hero-stat-label">Biens gérés</div>
                </div>
                <div className="lp-hero-stat">
                  <div className="lp-hero-stat-value">{Math.floor(animatedCounts.satisfaction)}%</div>
                  <div className="lp-hero-stat-label">Satisfaction client</div>
                </div>
              </div>
            </div>

            <div className="lp-hero-visual">
              <div className="lp-hero-mockup">
                <div className="lp-hero-mockup-header">
                  <div className="lp-mockup-dot" style={{ background: '#ef4444' }} />
                  <div className="lp-mockup-dot" style={{ background: '#f59e0b' }} />
                  <div className="lp-mockup-dot" style={{ background: '#22c55e' }} />
                  <span>Portail Immobilier & Annonces</span>
                </div>
                <div className="lp-mockup-stat-grid">
                  <div className="lp-mockup-stat"><div className="lp-mockup-stat-val">47</div><div>Biens récents</div></div>
                  <div className="lp-mockup-stat"><div className="lp-mockup-stat-val">12</div><div>Quartiers</div></div>
                  <div className="lp-mockup-stat"><div className="lp-mockup-stat-val">2.4M</div><div>Volume XOF</div></div>
                  <div className="lp-mockup-stat"><div className="lp-mockup-stat-val">100%</div><div>Vérifiés</div></div>
                </div>
                <div className="lp-mockup-list">
                  {[
                    { icon: '🏠', color: '#dbeafe', title: 'Villa 3 Chambres Salon - ACI 2000', sub: 'À louer · 450 000 XOF/mois', badge: 'Vedette', badgeColor: '#dcfce7', badgeText: '#166534' },
                    { icon: '🏢', color: '#fce7f3', title: 'Appartement 2 Chambres Salon - Badalabougou', sub: 'À louer · 200 000 XOF/mois', badge: 'Nouveau', badgeColor: '#ede9fe', badgeText: '#6d28d9' },
                    { icon: '🏗️', color: '#fef9c3', title: 'Terrain Titre Foncier (500m²) - Titibougou', sub: 'À vendre · 18 500 000 XOF', badge: 'Vente TF', badgeColor: '#ffedd5', badgeText: '#c2410c' },
                  ].map((item, i) => (
                    <div key={i} className="lp-mockup-item">
                      <div className="lp-mockup-item-icon" style={{ background: item.color }}>{item.icon}</div>
                      <div className="lp-mockup-item-text">
                        <div className="lp-mockup-item-title">{item.title}</div>
                        <div className="lp-mockup-item-sub">{item.sub}</div>
                      </div>
                      <span className="lp-mockup-item-badge" style={{ background: item.badgeColor, color: item.badgeText }}>{item.badge}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="lp-hero-bg-gradient" />
      </section>

      {/* STATS */}
      <section className="lp-stats" ref={statsRef}>
        <div className="lp-container">
          <div className="lp-stats-grid">
            <div className="lp-stat-item"><div className="lp-stat-number">{Math.floor(animatedCounts.agencies)}<span>+</span></div><div>Agences immobilières</div></div>
            <div className="lp-stat-item"><div className="lp-stat-number">{Math.floor(animatedCounts.properties / 1000)}<span>K+</span></div><div>Biens gérés</div></div>
            <div className="lp-stat-item"><div className="lp-stat-number">{Math.floor(animatedCounts.satisfaction)}<span>%</span></div><div>Taux de satisfaction</div></div>
            <div className="lp-stat-item"><div className="lp-stat-number">{Math.floor(animatedCounts.countries)}<span>pays</span></div><div>Présence en Afrique</div></div>
          </div>
        </div>
      </section>

      {/* ============================================================
          SECTION BIENS AMÉLIORÉE AVEC BARRE DE RECHERCHE COMPLÈTE
          ============================================================ */}
      <section className="lp-biens-section" id="biens">
        <div className="lp-container">
          {/* Header */}
          <div className="lp-biens-header">
            <div className="lp-biens-tag">Recherche Immobilière</div>
            <h2 className="lp-biens-title">Trouvez votre <span>bien idéal</span></h2>
            <p className="lp-biens-subtitle">
              Recherchez par ville, quartier, nombre de chambres salon, budget ou type de bien au Mali.
            </p>
          </div>

          {/* ── BARRE DE RECHERCHE PRINCIPALE AVANCÉE ── */}
          <div className="lp-search-wrapper">
            <div className="lp-search-main-bar">
              {/* Saisie plein texte & sémantique */}
              <div className="lp-search-input-field">
                <FiSearch color="#2563eb" size={18} />
                <input
                  type="text"
                  placeholder="Ex: 2 chambres salon, appartement, villa ACI 2000, Badalabougou, meublé..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', padding: 2 }}>
                    ✕
                  </button>
                )}
              </div>

              {/* Sélecteur Ville */}
              <div className="lp-search-select-field">
                <FiMapPin color="#64748b" size={16} />
                <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)}>
                  <option value="">Toutes les villes</option>
                  {MALI_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Sélecteur Quartier */}
              <div className="lp-search-select-field">
                <span style={{ fontSize: 13 }}>📍</span>
                <select value={selectedNeighborhood} onChange={(e) => setSelectedNeighborhood(e.target.value)}>
                  <option value="">Tous les quartiers</option>
                  {availableNeighborhoods.map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>

              {/* Sélecteur Type de Bien */}
              <div className="lp-search-select-field">
                <FiHome color="#64748b" size={16} />
                <select value={selectedPropertyType} onChange={(e) => setSelectedPropertyType(e.target.value)}>
                  {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              {/* Actions & Bouton Filtres Avancés */}
              <div className="lp-search-actions">
                <button
                  type="button"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={`lp-btn-filters-toggle ${showAdvancedFilters || activeFiltersCount > 0 ? 'active' : ''}`}
                >
                  <FiSliders size={16} />
                  <span>Filtres</span>
                  {activeFiltersCount > 0 && (
                    <span className="lp-filter-count-badge">{activeFiltersCount}</span>
                  )}
                </button>
              </div>
            </div>

            {/* ── PANNEAU DÉPLIABLE DES FILTRES AVANCÉS ── */}
            {showAdvancedFilters && (
              <div className="lp-advanced-filters-panel">
                <div className="lp-advanced-filters-grid">
                  {/* Pièces / Chambres */}
                  <div className="lp-filter-group">
                    <label className="lp-filter-label">Pièces & Chambres</label>
                    <select
                      className="lp-filter-select"
                      value={selectedRooms}
                      onChange={(e) => setSelectedRooms(e.target.value)}
                    >
                      {ROOM_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>

                  {/* Transaction (Location / Vente) */}
                  <div className="lp-filter-group">
                    <label className="lp-filter-label">Type d'opération</label>
                    <select
                      className="lp-filter-select"
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      <option value="">Toutes (Location & Vente)</option>
                      <option value="rent">🔑 Location uniquement</option>
                      <option value="sale">🏷️ Vente uniquement</option>
                    </select>
                  </div>

                  {/* Prix Min */}
                  <div className="lp-filter-group">
                    <label className="lp-filter-label">Budget Min (XOF)</label>
                    <input
                      type="number"
                      placeholder="Ex: 50 000"
                      className="lp-filter-input"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                    />
                  </div>

                  {/* Prix Max */}
                  <div className="lp-filter-group">
                    <label className="lp-filter-label">Budget Max (XOF)</label>
                    <input
                      type="number"
                      placeholder="Ex: 500 000"
                      className="lp-filter-input"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                    />
                  </div>

                  {/* Superficie Min */}
                  <div className="lp-filter-group">
                    <label className="lp-filter-label">Superficie Min (m²)</label>
                    <input
                      type="number"
                      placeholder="Ex: 100"
                      className="lp-filter-input"
                      value={minSurface}
                      onChange={(e) => setMinSurface(e.target.value)}
                    />
                  </div>

                  {/* Tri */}
                  <div className="lp-filter-group">
                    <label className="lp-filter-label">Trier par</label>
                    <select
                      className="lp-filter-select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="featured">⭐ En vedette d'abord</option>
                      <option value="recent">🕐 Plus récents</option>
                      <option value="price_asc">💵 Prix : Croissant</option>
                      <option value="price_desc">💎 Prix : Décroissant</option>
                      <option value="views">🔥 Plus consultés</option>
                    </select>
                  </div>

                  {/* Checkbox Meublé */}
                  <label className="lp-checkbox-group">
                    <input
                      type="checkbox"
                      checked={isFurnishedOnly}
                      onChange={(e) => setIsFurnishedOnly(e.target.checked)}
                    />
                    <span>🛋️ Meublé uniquement</span>
                  </label>

                  {/* Checkbox Vidéo */}
                  <label className="lp-checkbox-group">
                    <input
                      type="checkbox"
                      checked={hasVideoOnly}
                      onChange={(e) => setHasVideoOnly(e.target.checked)}
                    />
                    <span>🎥 Avec vidéo de visite</span>
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                  <button onClick={resetAllFilters} className="lp-reset-btn">
                    Réinitialiser tous les filtres
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── BARRE DE RÉSULTATS, CHIPS & BASCULE VUE CARTE ── */}
          <div className="lp-results-summary-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div className="lp-results-count">
                <span>🏠</span>
                <span>{filteredAnnouncements.length} bien(s) disponible(s)</span>
              </div>

              {/* Chips des filtres actifs */}
              {activeFiltersCount > 0 && (
                <div className="lp-active-chips">
                  {searchQuery && (
                    <span className="lp-chip">
                      🔍 "{searchQuery}" <span className="lp-chip-remove" onClick={() => setSearchQuery('')}>✕</span>
                    </span>
                  )}
                  {selectedCity && (
                    <span className="lp-chip">
                      📍 {selectedCity} <span className="lp-chip-remove" onClick={() => setSelectedCity('')}>✕</span>
                    </span>
                  )}
                  {selectedNeighborhood && (
                    <span className="lp-chip">
                      📌 {selectedNeighborhood} <span className="lp-chip-remove" onClick={() => setSelectedNeighborhood('')}>✕</span>
                    </span>
                  )}
                  {selectedPropertyType && (
                    <span className="lp-chip">
                      🏷️ {selectedPropertyType} <span className="lp-chip-remove" onClick={() => setSelectedPropertyType('')}>✕</span>
                    </span>
                  )}
                  {selectedRooms && (
                    <span className="lp-chip">
                      🛏️ {ROOM_OPTIONS.find(r => r.value === selectedRooms)?.label || selectedRooms} <span className="lp-chip-remove" onClick={() => setSelectedRooms('')}>✕</span>
                    </span>
                  )}
                  {filterType && (
                    <span className="lp-chip">
                      {filterType === 'rent' ? '🔑 Location' : '🏷️ Vente'} <span className="lp-chip-remove" onClick={() => setFilterType('')}>✕</span>
                    </span>
                  )}
                  {(minPrice || maxPrice) && (
                    <span className="lp-chip">
                      💰 {minPrice ? `${minPrice} XOF` : '0'} - {maxPrice ? `${maxPrice} XOF` : 'max'} <span className="lp-chip-remove" onClick={() => { setMinPrice(''); setMaxPrice(''); }}>✕</span>
                    </span>
                  )}
                  {isFurnishedOnly && (
                    <span className="lp-chip">
                      🛋️ Meublé <span className="lp-chip-remove" onClick={() => setIsFurnishedOnly(false)}>✕</span>
                    </span>
                  )}
                  {hasVideoOnly && (
                    <span className="lp-chip">
                      🎥 Vidéo <span className="lp-chip-remove" onClick={() => setHasVideoOnly(false)}>✕</span>
                    </span>
                  )}
                  <button onClick={resetAllFilters} className="lp-reset-btn">
                    Effacer tout
                  </button>
                </div>
              )}
            </div>

            {/* Bascule Grille / Carte */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setMapView(!mapView)}
                className={`lp-filter-btn map-btn ${mapView ? 'active' : ''}`}
                style={{ padding: '8px 18px', fontSize: 13 }}
              >
                <span>{mapView ? '📋 Vue Grille' : '🗺️ Vue Carte'}</span>
              </button>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="lp-biens-empty">
              <div className="spinner" style={{ margin: '0 auto 16px' }} />
              <p className="lp-biens-empty-title">Recherche des biens en cours...</p>
            </div>
          )}

          {/* Vue Carte Leaflet */}
          {!loading && mapView && (
            <div className="lp-map-container">
              <MapContainer
                center={[12.6392, -8.0029]}
                zoom={12}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap"
                />
                {filteredAnnouncements
                  .map(a => {
                    const coords = getAnnouncementCoords(a);
                    return coords ? { ...a, coords } : null;
                  })
                  .filter(Boolean)
                  .map(a => (
                    <Marker key={a.id} position={a.coords}>
                      <Popup>
                        <div style={{ minWidth: 220 }}>
                          <strong style={{ fontSize: 14, color: '#0f172a' }}>{a.title}</strong>
                          <p style={{ margin: '4px 0', fontSize: 12, color: '#64748b' }}>
                            {a.property_type}{a.unit_number ? ` (Unité ${a.unit_number})` : ''} — 📍 {a.neighborhood ? `${a.neighborhood}, ` : ''}{a.city}
                          </p>
                          <p style={{ margin: '4px 0 10px', fontSize: 15, fontWeight: 700, color: '#2563eb' }}>
                            {parseInt(a.price || 0, 10).toLocaleString('fr-FR')} XOF
                            {a.transaction_type === 'rent' && <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>/mois</span>}
                          </p>
                          <button
                            onClick={() => setSelectedPropertyDetail(a)}
                            className="btn btn-sm btn-primary"
                            style={{ width: '100%', padding: '6px 12px', fontSize: 12 }}
                          >
                            Voir détails complets
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
              </MapContainer>
            </div>
          )}

          {/* Vue Grille de Biens */}
          {!loading && !mapView && (
            <div className="lp-biens-grid">
              {filteredAnnouncements.map(a => (
                <AnnouncementCard
                  key={a.id}
                  announcement={a}
                  onContact={setContactModal}
                  onSelectDetail={setSelectedPropertyDetail}
                />
              ))}
            </div>
          )}

          {/* État vide */}
          {!loading && filteredAnnouncements.length === 0 && (
            <div className="lp-biens-empty">
              <div className="lp-biens-empty-icon">🏠</div>
              <p className="lp-biens-empty-title">Aucun bien ne correspond à votre recherche</p>
              <p className="lp-biens-empty-text">
                Essayez d'élargir votre recherche en modifiant le quartier, la ville, le type de bien ou en réinitialisant les filtres.
              </p>
              <button onClick={resetAllFilters} className="btn btn-outline" style={{ marginTop: 14 }}>
                <FiRefreshCw /> Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>
      </section>
      {/* ==================== FIN SECTION BIENS ==================== */}

      {/* FEATURES */}
      <section className="lp-section" id="features">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-tag">Fonctionnalités</div>
            <h2 className="lp-section-title">Tout ce dont votre agence <span className="gradient-text">a besoin</span></h2>
            <p className="lp-section-subtitle">Une plateforme complète conçue spécifiquement pour les agences immobilières africaines.</p>
          </div>
          <div className="lp-features-grid">
            {features.map((f, i) => (
              <div key={i} className="lp-feature-card">
                <div className="lp-feature-icon" style={{ background: f.color, color: f.iconColor }}>{f.icon}</div>
                <div className="lp-feature-title">{f.title}</div>
                <div className="lp-feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="lp-section lp-section-alt" id="how-it-works">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-tag">Comment ça marche</div>
            <h2 className="lp-section-title">Opérationnel en <span className="gradient-text">3 étapes</span></h2>
            <p className="lp-section-subtitle">Démarrez en quelques minutes, sans formation technique requise.</p>
          </div>
          <div className="lp-steps">
            {steps.map((s, i) => (
              <div key={i} className="lp-step">
                <div className="lp-step-number">{s.num}</div>
                <div className="lp-step-icon">{s.icon}</div>
                <div className="lp-step-title">{s.title}</div>
                <div className="lp-step-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="lp-section" id="pricing">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-tag">Tarifs</div>
            <h2 className="lp-section-title">Des prix <span className="gradient-text">transparents</span></h2>
            <p className="lp-section-subtitle">Commencez gratuitement. Évoluez selon vos besoins. Aucun frais caché.</p>
          </div>
          <div className="lp-pricing-grid">
            {plans.map((plan, i) => (
              <div key={i} className={`lp-pricing-card ${plan.featured ? 'featured' : ''}`}>
                {plan.badge && <div className="lp-pricing-badge">{plan.badge}</div>}
                <div className="lp-pricing-name">{plan.name}</div>
                <div className="lp-pricing-desc">{plan.desc}</div>
                <div className="lp-pricing-price">
                  <div className="lp-pricing-amount">
                    {plan.price === 'Gratuit' ? 'Gratuit' : <>{plan.price} <span>XOF</span></>}
                  </div>
                  <div className="lp-pricing-period">{plan.period}</div>
                </div>
                <ul className="lp-pricing-features">
                  {plan.features.map((f, j) => (
                    <li key={j}>
                      {f.ok ? <FiCheck className="check" /> : <FiX className="cross" />}
                      {f.text}
                    </li>
                  ))}
                </ul>
                <Link to={plan.name === 'Entreprise' || plan.name === 'Enterprise' ? '/contact' : '/register'} className={`lp-pricing-cta ${plan.ctaClass}`}>
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="lp-section lp-section-alt" id="testimonials">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-tag">Témoignages</div>
            <h2 className="lp-section-title">Ils nous font <span className="gradient-text">confiance</span></h2>
            <p className="lp-section-subtitle">Plus de 500 agences immobilières utilisent GestionImmo au quotidien.</p>
          </div>
          <div className="lp-testimonials-grid">
            {testimonials.map((t, i) => (
              <div key={i} className="lp-testimonial-card">
                <div className="lp-testimonial-stars">
                  {[...Array(5)].map((_, s) => (
                    <FiStar key={s} fill={s < t.stars ? '#fbbf24' : 'none'} color="#fbbf24" />
                  ))}
                </div>
                <div className="lp-testimonial-text">{t.text}</div>
                <div className="lp-testimonial-author">
                  <div className="lp-testimonial-avatar">{t.initials}</div>
                  <div>
                    <div className="lp-testimonial-name">{t.name}</div>
                    <div className="lp-testimonial-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="lp-cta">
        <div className="lp-container">
          <h2>Prêt à transformer votre agence ?</h2>
          <p>Rejoignez 500+ agences qui font confiance à GestionImmo. Essai gratuit 30 jours, sans carte bancaire.</p>
          <div className="lp-cta-actions">
            <Link to="/register" className="lp-btn-hero">
              Démarrer gratuitement <FiArrowRight />
            </Link>
            <Link to="/login" className="lp-btn-outline-white">
              Se connecter
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div className="lp-footer-brand">
              <Link to="/" className="lp-footer-logo">
                <div className="lp-footer-logo-icon">🏢</div>
                <span>Gestion<span>Immo</span></span>
              </Link>
              <p>La plateforme de gestion immobilière conçue pour les agences africaines. Simple, puissant, accessible.</p>
            </div>
            <div className="lp-footer-col">
              <h4>Produit</h4>
              <ul>
                <li><button onClick={() => scrollToSection('biens')}>Biens disponibles</button></li>
                <li><button onClick={() => scrollToSection('features')}>Fonctionnalités</button></li>
                <li><button onClick={() => scrollToSection('pricing')}>Tarifs</button></li>
                <li><button onClick={() => scrollToSection('how-it-works')}>Comment ça marche</button></li>
                <li><Link to="/register">Essai gratuit</Link></li>
              </ul>
            </div>
            <div className="lp-footer-col">
              <h4>Ressources</h4>
              <ul>
                <li><Link to="/login">Espace Agence</Link></li>
                <li><Link to="/portal/login">Portail Client</Link></li>
                <li><Link to="/support">Support</Link></li>
              </ul>
            </div>
            <div className="lp-footer-col">
              <h4>Légal</h4>
              <ul>
                <li><Link to="/terms">Conditions d'utilisation</Link></li>
                <li><Link to="/privacy">Politique de confidentialité</Link></li>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <p>© {new Date().getFullYear()} GestionImmo. Tous droits réservés.</p>
            <div className="lp-footer-social">
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Facebook">📘</a>
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Twitter">🐦</a>
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">🔗</a>
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">💬</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ============================================================
          MODAL DE FICHE DÉTAILLÉE COMPLÈTE DU BIEN
          ============================================================ */}
      {selectedPropertyDetail && (
        <PropertyDetailModal
          property={selectedPropertyDetail}
          onClose={() => setSelectedPropertyDetail(null)}
          onContact={(prop) => setContactModal(prop)}
        />
      )}

      {/* ============================================================
          MODAL DE CONTACT / DEMANDE DE VISITE
          ============================================================ */}
      {contactModal && (
        <div className="lp-modal-overlay" onClick={(e) => e.target === e.currentTarget && closeContactModal()}>
          <div className="lp-modal-content">
            {contactSent ? (
              <div className="lp-modal-success">
                <div className="lp-modal-success-icon">✅</div>
                <h3>Message envoyé !</h3>
                <p>L'agence vous contactera dans les plus brefs délais.</p>
                <button onClick={closeContactModal} className="lp-modal-success-btn">
                  Fermer
                </button>
              </div>
            ) : (
              <>
                <div className="lp-modal-header">
                  <div className="lp-modal-title-group">
                    <h3>Contacter l'agence</h3>
                    <p className="lp-modal-subtitle">
                      {contactModal.title} — {contactModal.neighborhood ? `${contactModal.neighborhood}, ` : ''}{contactModal.city}
                    </p>
                  </div>
                  <button onClick={closeContactModal} className="lp-modal-close">
                    ✕
                  </button>
                </div>

                <div className="lp-modal-body">
                  {/* Actions rapides */}
                  <div className="lp-modal-quick-actions">
                    {contactModal.agency_phone && (
                      <div className="lp-modal-phone-box">
                        <span className="lp-modal-phone-icon">📞</span>
                        <div>
                          <div className="lp-modal-phone-label">Appeler directement</div>
                          <a href={`tel:${contactModal.agency_phone}`} className="lp-modal-phone-link">
                            {contactModal.agency_phone}
                          </a>
                        </div>
                      </div>
                    )}
                    {contactModal.agency_phone && (
                      <a
                        href={`https://wa.me/${contactModal.agency_phone.replace(/\D/g, '')}?text=${encodeURIComponent(
                          `Bonjour, je suis intéressé par votre annonce : ${contactModal.title} (${contactModal.city} ${contactModal.neighborhood || ''}). Pouvez-vous me donner plus d'informations ?`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-modal-whatsapp-btn"
                      >
                        <span>💬</span> Contacter sur WhatsApp
                      </a>
                    )}
                  </div>

                  {/* Formulaire */}
                  <form onSubmit={handleContactSubmit}>
                    <div className="lp-form-group">
                      <label className="lp-form-label">Votre nom *</label>
                      <input
                        required
                        value={contactForm.name}
                        onChange={e => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder="Amadou Diallo"
                        className="lp-form-input"
                      />
                    </div>
                    <div className="lp-form-group">
                      <label className="lp-form-label">Téléphone *</label>
                      <input
                        required
                        value={contactForm.phone}
                        onChange={e => setContactForm({ ...contactForm, phone: e.target.value })}
                        placeholder="+223 XX XX XX XX"
                        className="lp-form-input"
                      />
                    </div>
                    <div className="lp-form-group">
                      <label className="lp-form-label">Email</label>
                      <input
                        type="email"
                        value={contactForm.email}
                        onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder="amadou@email.com"
                        className="lp-form-input"
                      />
                    </div>
                    <div className="lp-form-group">
                      <label className="lp-form-label">Message</label>
                      <textarea
                        value={contactForm.message}
                        onChange={e => setContactForm({ ...contactForm, message: e.target.value })}
                        placeholder="Bonjour, je souhaite visiter ce bien..."
                        rows={3}
                        className="lp-form-textarea"
                      />
                    </div>
                    <button type="submit" className="lp-form-submit">
                      Envoyer ma demande
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;